<template>
  <b-modal
    id="modal-event-stumble"
    class="custom-modal3"
    :hide-footer="true"
    :hide-backdrop="true"
    modal-class="custom-modal3"
    content-class="rectangular-dark-blue-modal"
  >
    <template slot="modal-header" slot-scope="{ close }">
      <a href="#" class="close-button" @click.prevent="close()"> </a>
    </template>

    <template slot="default">
      <div class="headline1">{{ $t("event.stumble.headline") }}</div>

      <div class="text" v-html="text"></div>

      <!-- Up to 4 rewards; icon and amount shrink the more there are. -->
      <div
        class="premium-rewards"
        :class="`reward-count-${Math.min(premiumRewardList.length, 4)}`"
      >
        <div
          v-for="reward in premiumRewardList.slice(0, 4)"
          :key="reward.id"
          class="premium-reward"
        >
          <img
            v-if="rewardIcon(reward)"
            :src="rewardIcon(reward)"
            alt=""
            class="reward-icon"
          />
          <span class="reward-amount">{{ formatNumber(reward.amount) }}</span>
        </div>
      </div>

      <div class="button-row">
        <div class="button-column">
          <button
            class="neutral-button-blue"
            :disabled="busy"
            @click="$emit('claim-free')"
          >
            {{ $t("event.stumble.button_free") }}
          </button>
        </div>
        <div class="button-column">
          <div class="upsell-line">{{ $t("event.stumble.upsell") }}</div>
          <button
            class="ok-button-green"
            :disabled="busy"
            @click="$emit('get-premium')"
          >
            {{ premiumPrice || $t("event.stumble.button_premium") }}
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  escapeHtml,
  freeRewards,
  premiumRewards,
} from "@/utils/eventSystem.js";

// Wraps an already escaped value in <b>; i18next must not escape it again.
const bold = (value) => `<b>${value}</b>`;
const raw = { interpolation: { escapeValue: false } };

// Last upsell before the free reward is booked on its own. Layout from
// modal-change-vanilla-mission.
export default {
  name: "ModalEventStumble",
  props: {
    event: { type: Object, default: null },
    // Formatted web price of the premium package, e.g. "2,99€".
    premiumPrice: { type: String, default: "" },
    busy: { type: Boolean, default: false },
  },
  computed: {
    freeRewardList() {
      return freeRewards(this.event);
    },
    premiumRewardList() {
      return premiumRewards(this.event);
    },
    freeLabel() {
      return this.freeRewardList
        .map((r) => `${this.formatNumber(r.amount)} ${this.assetTitle(r)}`)
        .join(", ");
    },
    // Asset titles come from the backend, so escape before v-html.
    text() {
      const free = this.$t("event.stumble.text", {
        0: bold(escapeHtml(this.freeLabel)),
        ...raw,
      });
      if (!this.percentMore) return free;
      const percent = this.$t("event.stumble.text_percent", {
        0: bold(this.percentMore),
        ...raw,
      });
      return `${free} ${percent}`;
    },
    // How much bigger the premium reward is than the free one, in percent.
    // Only meaningful when both tiers pay out in a single, identical asset.
    percentMore() {
      const [free] = this.freeRewardList;
      const [premium] = this.premiumRewardList;
      if (
        this.freeRewardList.length !== 1 ||
        this.premiumRewardList.length !== 1 ||
        free.asset_id !== premium.asset_id
      ) {
        return "";
      }
      const freeAmount = Number(free.amount) || 0;
      if (freeAmount <= 0) return "";
      const percent = Math.round((Number(premium.amount) / freeAmount) * 100);
      return percent > 0 ? this.formatNumber(percent) : "";
    },
  },
  methods: {
    assetTitle(reward) {
      const asset = (reward && reward.asset) || {};
      return asset.title || "";
    },
    rewardIcon(reward) {
      const asset = (reward && reward.asset) || {};
      return (asset.image_urls && asset.image_urls.image_url) || "";
    },
  },
};
</script>

<style lang="scss">
$modal-width: 860px;
$event-center-y: calc(
  50px + 1.75rem +
    min(87.5rem * 1080 / 2340, (100vw - 2rem) * 1080 / 2340, 100vh - 100px) / 2
);

#modal-event-stumble * {
  font-family: "Roboto", sans-serif;
}

#modal-event-stumble {
  .modal-body {
    margin-top: -15px;
    padding-bottom: 40px;
  }

  // custom-modal3 stretches dialog + content to 100% of the viewport; this
  // modal sizes to its content instead.
  .modal-dialog {
    max-width: $modal-width;
    height: auto;
    margin-top: $event-center-y;
    transform: translateY(calc(-50% - 50px));
  }

  &.show .modal-dialog {
    transform: translateY(-50%);
  }

  .modal-content {
    height: auto;
  }

  .button-row button {
    font-weight: 400;
    font-size: 24px;
    width: 288px;
    height: 44px;
  }

  // Shared blue button, squared off to match the green one beside it.
  .button-row .neutral-button-blue {
    min-width: 0;
    border-radius: 4px;
    text-shadow: none;
  }
}
</style>

<style lang="scss" scoped>
#modal-event-stumble {
  .headline1 {
    font-weight: 700;
    color: white;
    font-size: 32px;
    text-transform: uppercase;
  }

  .text {
    margin: 12px auto 0;
    max-width: 700px;
    font-weight: 400;
    font-size: 22px;
    color: $text-light-gray;
    line-height: 30px;
    padding: 0 20px;

    b {
      font-weight: 700;
      color: white;
    }
  }

  .premium-rewards {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin: 20px 0 12px;
  }

  .premium-reward {
    display: flex;
    flex-direction: column;
    align-items: center;

    .reward-icon {
      width: 96px;
      height: 96px;
      object-fit: contain;
    }

    .reward-amount {
      margin-top: 4px;
      font-weight: 700;
      font-size: 28px;
      color: white;
    }
  }

  // Shrink icon and amount the more rewards there are.
  @each $count, $icon, $font in (2, 84px, 26px), (3, 72px, 24px),
    (4, 60px, 22px)
  {
    .reward-count-#{$count} .premium-reward {
      .reward-icon {
        width: $icon;
        height: $icon;
      }

      .reward-amount {
        font-size: $font;
      }
    }
  }

  // Both buttons share the bottom line; the upsell line sits above the right.
  .button-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 0 40px;
  }

  .button-column {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .upsell-line {
    margin-bottom: 8px;
    font-weight: 700;
    font-size: 24px;
    color: white;
  }
}
</style>
